import {
  selectorFamilyWithoutCache as selectorFamily,
  selectorWithoutCache as selector,
} from "util/RecoilWithoutCache";
import {
  ChangeTimezoneLocalstorageKey,
  getTimezonePostParamValue,
  Last24HOption,
  SupportedChangeTimezone,
} from "constants/base";
import { atom, useRecoilValueLoadable } from "recoil";
import { getOpenPrice, OpenPriceMap } from "../../api/ct";
import { isSuccess } from "../../util/Common";
import { useMemo } from "react";
import _isNull from "lodash/isNull";
import { isBrowserEnv } from "@app/utils/constants";
import Decimal from "decimal.js";
import {
  UserPreferenceSettingEnum,
  UserPreferenceSettingState,
} from "../data/User";

function getLocalChangeTimezone() {
  let temp = isBrowserEnv
    ? localStorage.getItem(ChangeTimezoneLocalstorageKey)
    : "";
  // 检查一下有没有被修改
  if (
    SupportedChangeTimezone.some((i) => String(i) === temp) ||
    temp === Last24HOption
  ) {
    return temp;
  } else {
    return Last24HOption;
  }
}

const ChangeTimezoneState = atom<string>({
  key: "ChangeTimezoneState",
  default: getLocalChangeTimezone(),
});

/**
 * 涨跌幅专用的时区选择器
 */
export const ChangeTimezoneSelector = selector<string>({
  key: "ChangeTimezoneSelector",
  get: ({ get }) => {
    return get(ChangeTimezoneState);
  },
  set: ({ set }, newValue: string) => {
    try {
      localStorage.setItem(ChangeTimezoneLocalstorageKey, newValue);
      set(ChangeTimezoneState, newValue);
    } catch (e) {
      console.error("set ChangeTimezoneState error", e);
    }
  },
});

/**
 * 接口获取开盘价kv集合
 */
export const OpenPriceMapSelectorFamily = selectorFamily<
  OpenPriceMap,
  { business: string; timezone: number }
>({
  key: "OpenPriceSelector",
  get: (param: { business: string; timezone: number }) => async () => {
    const { business, timezone } = param;
    try {
      let res = await getOpenPrice({
        business,
        timezone: getTimezonePostParamValue(timezone),
      });
      if (isSuccess(res?.code)) {
        return res?.data?.symbolKlines || {};
      } else {
        return {};
      }
    } catch (e) {
      return {};
    }
  },
});

/**
 * 获取某交易对的开盘价
 */
export const OpenPriceForCalcChange = selectorFamily<
  number,
  { symbolCode: string; symbolId?: string }
>({
  key: "OpenPriceForCalcChange",
  get:
    (params: { symbolCode: string; symbolId?: string }) =>
    async ({ get }) => {
      let { symbolCode, symbolId } = params;
      const [_symbolCode, bl] = symbolId.split("_");
      symbolCode = symbolCode ? symbolCode : _symbolCode;
      const business = bl === "SPBL" ? "sp" : "mc";
      const timezone = get(UserPreferenceSettingState)[
        UserPreferenceSettingEnum.BENCHMARK_TIMEZONE
      ];

      if (timezone === Last24HOption) {
        // 通过NaN来标识最近二十四小时
        return null;
      }
      const openPriceMap = get(
        OpenPriceMapSelectorFamily({ business, timezone: Number(timezone) })
      );
      return Number(openPriceMap[symbolCode]?.openToday || 0);
    },
});

export const OpenPriceMapForCalcChange = selectorFamily({
  key: "OpenPriceMapForCalcChange",
  get:
    (businessType: "sp" | "mc") =>
    async ({ get }) => {
      const timezone = get(UserPreferenceSettingState)[
        UserPreferenceSettingEnum.BENCHMARK_TIMEZONE
      ];
      if (timezone === Last24HOption) {
        // 通过NaN来标识最近二十四小时
        return {};
      }
      return get(
        OpenPriceMapSelectorFamily({
          business: businessType,
          timezone: Number(timezone),
        })
      );
    },
});

// 数值转换（小数点位数过多时，不使用科学计数法）
function toNum(x) {
  if (x?.toString().includes("e") && Math.abs(x) < 1.0) {
    var e = parseInt(x.toString().split("e-")[1]);
    if (e) {
      x = new Decimal(x).mul(Math.pow(10, e - 1)).toNumber();
      x = "0." + new Array(e).join("0") + x.toString().substring(2);
    }
  }
  return x;
}

/**
 * @description 计算24h涨跌额(现货业务-币对信息区-24h涨跌额处使用，计算24h涨跌额)
 * @param businessType sp：现货
 * @returns 24h涨跌额
 */
export function useUniversalChangePriceCalculator(
  businessType: "sp" | "mc"
): any {
  if (!isBrowserEnv) {
    return (
      symbolId: string,
      price: number | string,
      before24Open: number | string
    ) => {
      return 0;
    };
  }
  const openPriceMap = useRecoilValueLoadable(
    OpenPriceMapForCalcChange(businessType)
  );
  return useMemo(
    () =>
      (
        symbolId: string,
        price: number | string,
        before24Open: number | string
      ) => {
        if (
          openPriceMap.state === "loading" ||
          openPriceMap.state === "hasError"
        ) {
          return 0;
        }
        let _before24Open = Number(before24Open);
        let open = isNaN(Number(openPriceMap.contents[symbolId]?.openToday))
          ? _before24Open
          : Number(openPriceMap.contents[symbolId]?.openToday);
        if (open === 0) return 0;
        const result = new Decimal(Number(price)).sub(open).toFixed();
        return result; //toNum(result)
      },
    [openPriceMap]
  );
}

export function useUniversalChangeCalculator(businessType: "sp" | "mc"): any {
  if (!isBrowserEnv) {
    return (
      symbolId: string,
      price: number | string,
      before24Open: number | string
    ) => {
      return 0;
    };
  }
  // 可能得内存泄漏点
  const openPriceMap = useRecoilValueLoadable(
    OpenPriceMapForCalcChange(businessType)
  );
  return useMemo(
    () =>
      (
        symbolId: string,
        price: number | string,
        before24Open: number | string
      ) => {
        if (
          openPriceMap.state === "loading" ||
          openPriceMap.state === "hasError"
        ) {
          return 0;
        }
        let _before24Open = Number(before24Open);
        let open = isNaN(Number(openPriceMap.contents[symbolId]?.openToday))
          ? _before24Open
          : Number(openPriceMap.contents[symbolId]?.openToday);
        if (open === 0) return 0;
        return ((Number(price) - open) / open) * 100;
      },
    [openPriceMap]
  );
}

/**
 * 计算涨跌幅的钩子
 * @param symbolId
 * @param price
 * @param before24Open
 */
export function useCalcChange(
  symbolId: string,
  price: number | string,
  before24Open: number | string,
  symbolCode?: string
) {
  if (!isBrowserEnv) {
    return { calcChangeValue: 0, calcChange: 0 };
  }
  // todo
  // 由于获取某交易对的开盘价的 selector 使用的是 ticker 中的 symbolCode 作为键值，在交割合约中会出现分割的错误
  // 因此在合约的涨跌幅中新增一个 symbolCode 参数

  const openPriceForCalcChange = useRecoilValueLoadable(
    // 可能内存泄漏点
    OpenPriceForCalcChange({
      symbolId,
      symbolCode,
    })
  );

  return useMemo<{ calcChangeValue: number; calcChange: number }>(() => {
    if (
      openPriceForCalcChange.state === "loading" ||
      openPriceForCalcChange.state === "hasError"
    ) {
      return { calcChangeValue: 0, calcChange: 0 };
    }
    let _before24Open = Number(before24Open);
    let open = _isNull(openPriceForCalcChange.contents)
      ? _before24Open
      : Number(openPriceForCalcChange.contents);
    if (open === 0) return { calcChangeValue: 0, calcChange: 0 };
    return {
      calcChangeValue: Number(price) - open,
      calcChange: ((Number(price) - open) / open) * 100,
    };
  }, [before24Open, price, openPriceForCalcChange]);
}

export const handleChgUTC = ({
  symbolId,
  price,
  before24Open,
  timezone,
  openList,
}: {
  symbolId: string;
  price: number | string;
  before24Open: number | string;
  timezone: string;
  openList: any;
}) => {
  let open: any = isNaN(openList) ? before24Open : Number(openList);
  if (open === 0) return 0;
  return ((Number(price) - open) / open) * 100;
};

/**
 * 是否在app端环境
 */
export const isAppEnv = atom({
  key: "app-environment",
  default: false,
});

/**
 * 当前币对价格
 */
export const coinPriceState = atom({
  key: "coin-price",
  default: {
    price: 0,
    coin: "",
  },
});
